import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, AlertTriangle, Loader2, Info } from "lucide-react";
import api from "@/lib/api";

const digits = (v) => (v || "").replace(/\D/g, "");

// Consulta el padrón DGII cuando el número tiene 9 u 11 dígitos.
function usePadron(ident) {
  const [state, setState] = useState({ status: "idle", reg: null });
  useEffect(() => {
    const d = digits(ident);
    if (![9, 11].includes(d.length)) { setState({ status: d ? "incomplete" : "idle", reg: null }); return undefined; }
    setState({ status: "loading", reg: null });
    const t = setTimeout(() => {
      api.get(`/dgii/rnc/lookup/${d}`).then(({ data }) => setState({ status: "found", reg: data }))
        .catch(() => setState({ status: "notfound", reg: null }));
    }, 350);
    return () => clearTimeout(t);
  }, [ident]);
  return state;
}

function PadronNotice({ check, rnc }) {
  if (check.status === "loading") return <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Buscando en el padrón DGII…</p>;
  if (check.status === "found") {
    const active = (check.reg.estado || "ACTIVO").toUpperCase() === "ACTIVO";
    return (
      <p className={`text-xs flex items-start gap-1 ${active ? "text-success" : "text-amber-700"}`} data-testid="occ-buyer-padron-found">
        {active ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
        DGII: {check.reg.razon_social} · {check.reg.estado || "ACTIVO"}{!active && " — la DGII podría rechazar el e-CF"}
      </p>
    );
  }
  if (check.status === "notfound") {
    return (
      <p className="text-xs text-amber-700 flex items-start gap-1" data-testid="occ-buyer-padron-notfound">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        {digits(rnc).length === 9 ? "Este RNC no está en el padrón descargado." : "Cédula no inscrita como contribuyente."} Escribe el nombre a mano.
      </p>
    );
  }
  if (check.status === "incomplete") return <p className="text-xs text-muted-foreground">RNC 9 dígitos o cédula 11 dígitos.</p>;
  return null;
}

export default function OccasionalBuyerDialog({ open, onOpenChange, initialRnc = "", initial = null, onConfirm }) {
  const [rnc, setRnc] = useState("");
  const [name, setName] = useState("");
  const [save, setSave] = useState(false);
  const check = usePadron(rnc);

  useEffect(() => {
    if (!open) return;
    setRnc(initial?.rnc_cedula || initialRnc || "");
    setName(initial?.name || "");
    setSave(!!initial?.save_occasional);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (check.reg) setName(check.reg.razon_social || ""); }, [check.reg]);

  const d = digits(rnc);
  const fromPadron = check.status === "found";
  const canSave = !(d.length === 9 && check.status === "notfound");
  useEffect(() => { if (!canSave) setSave(false); }, [canSave]);
  const valid = [9, 11].includes(d.length) && name.trim().length > 1 && check.status !== "loading";

  const confirm = () => {
    onConfirm({ id: null, occasional: true, name: name.trim(), rnc_cedula: d, save_occasional: save && canSave });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="occ-buyer-dialog">
        <DialogHeader>
          <DialogTitle>Cliente ocasional</DialogTitle>
          <DialogDescription>Factura con el RNC o la cédula del comprador sin registrarlo como cliente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline">RNC o cédula</Label>
            <Input autoFocus className="mt-1 font-mono" value={rnc} onChange={(e) => setRnc(e.target.value)}
              placeholder="1-01-00000-0 o 001-0000000-0" data-testid="occ-buyer-rnc" />
            <div className="mt-1.5"><PadronNotice check={check} rnc={rnc} /></div>
          </div>
          <div>
            <Label className="overline">Nombre o razón social</Label>
            <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} readOnly={fromPadron}
              placeholder="Como aparece en la DGII" data-testid="occ-buyer-name" />
            {fromPadron && <p className="text-[11px] text-muted-foreground mt-1">Tomado del padrón DGII para que el e-CF no sea rechazado.</p>}
          </div>
          <label className={`flex items-start gap-2 text-sm ${canSave ? "" : "opacity-50"}`}>
            <Checkbox checked={save} disabled={!canSave} onCheckedChange={(v) => setSave(!!v)} className="mt-0.5" data-testid="occ-buyer-save" />
            <span>Guardar como cliente
              <span className="block text-[11px] text-muted-foreground">Se guarda solo de contado y con E31/E32. Crédito u otros comprobantes los asigna un administrador en Clientes.</span>
            </span>
          </label>
          <div className="rounded-md bg-muted/60 p-2.5 text-[11px] text-muted-foreground flex gap-2">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Solo ventas de contado con E31 (crédito fiscal) o E32 (consumo).
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="occ-buyer-cancel">Cancelar</Button>
          <Button disabled={!valid} onClick={confirm} data-testid="occ-buyer-confirm">Usar este cliente</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
